import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  TextField,
  Checkbox,
  Button,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  type SelectChangeEvent,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Assignment as AssignmentIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { useFirm } from "../contexts/FirmContext";
import { useDebounce } from "../hooks/useDebounce";
import {
  getOpsJobs,
  useListAccessibleFirms,
  useListAllUsers,
  useAssignJob,
  useReassignJob,
  useReleaseJobAssignment,
  JobStatus,
  UserRole,
  type OpsJobItem,
  type ErrorResponse,
} from "@taxassist/api-client";
import { StatusBadge } from "./jobs/StatusBadge";
import { UserDisplay } from "./common/UserDisplay";
import { JobAssignmentDialog } from "./jobs/JobAssignmentDialog";
import { getJobEtaDisplay } from "../utils/jobUtils";
import { formatDate } from "../utils/dateUtils";

interface AllJobsViewProps {
  onJobSelected?: (jobId: string, firmId: string) => void;
  selectedJobId?: string | null;
  initialStatusFilter?: string;
  context?: 'ops' | 'client';
  showOnlyInactive?: boolean; // True = show only inactive jobs (DONE, CANCELLED, ARCHIVED)
}

export const AllJobsView: React.FC<AllJobsViewProps> = ({
  onJobSelected,
  selectedJobId,
  initialStatusFilter,
  showOnlyInactive = false,
  context = 'client',
}) => {
  const { user, isAuthenticated } = useAuth();
  const { availableFirms } = useFirm();
  const [statusFilter, setStatusFilter] = useState<string>(
    initialStatusFilter || "",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedReviewer, setSelectedReviewer] = useState<string>("");
  const [jobAssignmentDialogOpen, setJobAssignmentDialogOpen] = useState(false);
  const [selectedJobForAssignment, setSelectedJobForAssignment] =
    useState<OpsJobItem | null>(null);

  // Check if user has access
  const hasAccess =
    user?.role === UserRole.admin || user?.role === UserRole.reviewer;

  // Assignment mutation hooks
  const assignJobMutation = useAssignJob();
  const reassignJobMutation = useReassignJob();
  const releaseJobMutation = useReleaseJobAssignment();

  // Fetch reviewers for assignment dialog
  const { data: reviewersData } = useListAllUsers(
    hasAccess ? { roles: ["admin", "reviewer"], limit: 500 } : undefined,
  );

  // Fetch all firms for admin/reviewer to resolve firm names
  const { data: allFirmsResponse } = useListAccessibleFirms(
    hasAccess ? { limit: 500 } : undefined,
  );

  // Create firmId → firmName mapping
  const firmNameMap = React.useMemo(() => {
    const map = new Map<string, string>();

    // Add firms from FirmContext (for current user's firms)
    if (availableFirms) {
      availableFirms.forEach((firm) => {
        map.set(firm.firmId, firm.name);
      });
    }

    // Add firms from all firms API (for admin/reviewer)
    if (allFirmsResponse?.firms) {
      allFirmsResponse.firms.forEach((firm) => {
        map.set(firm.firmId, firm.name);
      });
    }

    return map;
  }, [availableFirms, allFirmsResponse?.firms]);

  // Helper function to get firm name
  const getFirmName = React.useCallback(
    (firmId: string) => {
      return firmNameMap.get(firmId) || firmId; // Fallback to firmId if name not found
    },
    [firmNameMap],
  );

  const debouncedSearch = useDebounce(searchTerm, 300);

  // Pre-resolve search term against the firm-name map so the backend can OR-match
  // jobs whose firmId is in this list (the Jobs table has no firm-name column).
  const matchingFirmIdsCsv = React.useMemo(() => {
    if (!debouncedSearch) return undefined;
    const needle = debouncedSearch.toLowerCase();
    const ids: string[] = [];
    firmNameMap.forEach((name, firmId) => {
      if (name.toLowerCase().includes(needle)) {
        ids.push(firmId);
      }
    });
    return ids.length > 0 ? ids.join(",") : undefined;
  }, [debouncedSearch, firmNameMap]);

  const baseParams = {
    limit: 25,
    ...(statusFilter && { status: statusFilter as any }),
    ...(showOnlyInactive && { inactive: "true" as const }),
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(matchingFirmIdsCsv && { firmIds: matchingFirmIdsCsv }),
  };

  // Cursor-based infinite query — fetches 50 at a time
  const {
    data: infiniteData,
    isLoading: loading,
    error: queryError,
    refetch: refetchJobs,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["ops-jobs", baseParams],
    queryFn: ({ pageParam }) =>
      getOpsJobs(pageParam ? { ...baseParams, nextToken: pageParam } : baseParams),
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.hasMore ? lastPage.pagination?.nextToken ?? undefined : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: isAuthenticated && hasAccess,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  const allJobs = useMemo(
    () => infiniteData?.pages.flatMap((p) => p.jobs) ?? [],
    [infiniteData],
  );

  // Jobs are already filtered server-side by search and status
  const filteredJobs = allJobs;

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleRefresh = () => {
    setError(null);
    refetchJobs();
  };

  // Individual job assignment handlers
  const handleJobAssignmentClick = (job: OpsJobItem) => {
    setSelectedJobForAssignment(job);
    setJobAssignmentDialogOpen(true);
  };

  const handleJobAssignmentDialogClose = () => {
    setJobAssignmentDialogOpen(false);
    setSelectedJobForAssignment(null);
  };

  const handleJobAssignmentSuccess = () => {
    refetchJobs(); // Refresh the jobs list
  };

  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allJobIds = new Set(filteredJobs.map((job) => job.jobId));
      setSelectedJobs(allJobIds);
    } else {
      setSelectedJobs(new Set());
    }
  };

  const handleSelectJob = (jobId: string) => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const handleClearSelection = () => {
    setSelectedJobs(new Set());
  };

  // Bulk action handlers
  const handleBulkAssign = () => {
    setIsAssignDialogOpen(true);
  };

  const handleAssignDialogClose = () => {
    setIsAssignDialogOpen(false);
    setSelectedReviewer("");
  };

  const handleConfirmAssign = async () => {
    try {
      const jobIds = Array.from(selectedJobs);

      if (jobIds.length === 0) {
        setError("No jobs selected");
        return;
      }

      const results = await Promise.allSettled(
        jobIds.map(async (jobId) => {
          const job = allJobs.find((j) => j.jobId === jobId);
          if (!job) {
            throw new Error(`Job ${jobId} not found`);
          }

          if (selectedReviewer === "") {
            // Release assignment (unassign)
            return await releaseJobMutation.mutateAsync({
              jobId,
              params: { reason: "Bulk unassignment" },
            });
          } else if (!job.assignedReviewer) {
            // Job is unassigned - assign it
            return await assignJobMutation.mutateAsync({
              jobId,
              data: {
                reviewerId: selectedReviewer,
                reason: "Bulk assignment",
              },
            });
          } else if (job.assignedReviewer !== selectedReviewer) {
            // Job is assigned to someone else - reassign it
            return await reassignJobMutation.mutateAsync({
              jobId,
              data: {
                reviewerId: selectedReviewer,
                reason: "Bulk reassignment",
              },
            });
          } else {
            // Job is already assigned to the selected reviewer - skip
            return Promise.resolve({ jobId, skipped: true });
          }
        }),
      );

      // Check for failures
      const failures = results.filter((result) => result.status === "rejected");
      if (failures.length > 0) {
        console.error("Some assignments failed:", failures);
        setError(
          `${failures.length} of ${jobIds.length} assignments failed. Please try again.`,
        );
      } else {
        // Success - refresh the jobs list
        await refetchJobs();
      }

      handleAssignDialogClose();
      handleClearSelection();
    } catch (error) {
      console.error("Bulk assignment failed:", error);
      setError("Failed to assign jobs. Please try again.");
    }
  };

  // Selection state computations
  const selectedCount = selectedJobs.size;
  const isAllSelected =
    selectedCount > 0 && selectedCount === filteredJobs.length;
  const isIndeterminate =
    selectedCount > 0 && selectedCount < filteredJobs.length;

  // Show unauthorized message if user doesn't have access
  if (!isAuthenticated || !hasAccess) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Unauthorized - You need REVIEWER or ADMIN access to view this page
        </Alert>
      </Box>
    );
  }

  // Show loading state
  if (loading && allJobs.length === 0) {
    return (
      <Box
        p={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (queryError) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          action={
            <IconButton color="inherit" size="small" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          }
        >
          Error loading jobs:{" "}
          {(queryError as ErrorResponse)?.error || "Unknown error"}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Box minWidth="300px" flex="1">
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Client, firm name, filing type..."
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Box>
            <Box minWidth="120px">
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value={JobStatus.TRIAGE}>Triage</MenuItem>
                  <MenuItem value={JobStatus.SUBMITTED}>Submitted</MenuItem>
                  <MenuItem value={JobStatus.PROCESSING}>Processing</MenuItem>
                  <MenuItem value={JobStatus.VERIFICATION}>
                    Verification
                  </MenuItem>
                  <MenuItem value={JobStatus.VERIFIED}>Verified</MenuItem>
                  <MenuItem value={JobStatus.DONE}>Done</MenuItem>
                  <MenuItem value={JobStatus.ERROR}>Error</MenuItem>
                  <MenuItem value={JobStatus.CANCELLED}>Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredJobs.length} loaded jobs
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Success/Error Messages */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Jobs Table */}
      {filteredJobs.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <SearchIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No active jobs found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || statusFilter
                  ? "Try adjusting your filters to see more results."
                  : "No active jobs are available at the moment."}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Bulk Actions Toolbar */}
          {selectedCount > 0 && (
            <Paper sx={{ mb: 2 }}>
              <Toolbar
                sx={{
                  pl: 2,
                  pr: 1,
                  bgcolor: (theme) => theme.palette.action.selected,
                }}
              >
                <Typography
                  sx={{ flex: "1 1 100%" }}
                  color="inherit"
                  variant="subtitle1"
                  component="div"
                >
                  {selectedCount} job{selectedCount === 1 ? "" : "s"} selected
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Assign to reviewer">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={handleBulkAssign}
                    >
                      Assign
                    </Button>
                  </Tooltip>
                  <Tooltip title="Clear selection">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleClearSelection}
                    >
                      Clear
                    </Button>
                  </Tooltip>
                </Box>
              </Toolbar>
            </Paper>
          )}

          <TableContainer component={Paper}>
            <Table
              sx={{
                "& .MuiTableCell-root": {
                  fontSize: "1rem", // same as body1 (16px by default)
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      inputProps={{
                        "aria-label": "select all jobs",
                      }}
                    />
                  </TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Firm</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Date Due</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredJobs.map((job) => (
                  <TableRow
                    key={job.jobId}
                    onClick={() => onJobSelected?.(job.jobId, job.firmId)}
                    sx={{
                      cursor: onJobSelected ? "pointer" : "default",
                      backgroundColor:
                        selectedJobId === job.jobId ? "#EDF7FF" : "inherit",
                      "&:hover": {
                        backgroundColor:
                          selectedJobId === job.jobId ? "#E3F2FD" : "#F5F5F5",
                      },
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        color="primary"
                        checked={selectedJobs.has(job.jobId)}
                        onChange={() => handleSelectJob(job.jobId)}
                        inputProps={{
                          "aria-labelledby": `job-${job.jobId}`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {job.clientName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getFirmName(job.firmId)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={job.status as any}
                        statusMapping={null}
                        job={job as any}
                        context={context}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <UserDisplay
                          userId={job.assignedReviewer}
                          variant="body2"
                          color="textPrimary"
                          contextFirmId={job.firmId}
                          fallback="Unassigned"
                        />
                        <Tooltip title="Assign/Reassign Job">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleJobAssignmentClick(job);
                            }}
                            sx={{
                              opacity: 0.7,
                              "&:hover": { opacity: 1 },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getJobEtaDisplay(job)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.submittedAt ? formatDate(job.submittedAt, 'MM/dd/yy hh:mm a') : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(job.createdAt, 'MM/dd/yy hh:mm a')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Load more */}
          {hasNextPage && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Button
                variant="outlined"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Assignment Dialog */}
      <Dialog
        open={isAssignDialogOpen}
        onClose={handleAssignDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AssignmentIcon />
            Assign Jobs to Reviewer
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Assign {selectedCount} selected job
              {selectedCount === 1 ? "" : "s"} to a reviewer
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Select Reviewer</InputLabel>
              <Select
                value={selectedReviewer}
                onChange={(e) => setSelectedReviewer(e.target.value)}
                label="Select Reviewer"
              >
                <MenuItem value="">
                  <em>Unassign</em>
                </MenuItem>
                {reviewersData?.users?.map((reviewer) => (
                  <MenuItem key={reviewer.userId} value={reviewer.userId}>
                    {reviewer.firstName} {reviewer.lastName} ({reviewer.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedJobs.size > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Selected Jobs:
                </Typography>
                <Box
                  sx={{
                    maxHeight: 200,
                    overflow: "auto",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {Array.from(selectedJobs).map((jobId) => {
                    const job = allJobs.find((j) => j.jobId === jobId);
                    return job ? (
                      <Box key={jobId} sx={{ py: 0.5, fontSize: "0.875rem" }}>
                        <strong>{job.clientName}</strong> - {job.filingType}{" "}
                        {job.taxYear}
                      </Box>
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignDialogClose}>Cancel</Button>
          <Button
            onClick={handleConfirmAssign}
            variant="contained"
            startIcon={<PersonAddIcon />}
          >
            {selectedReviewer === "" ? "Unassign Jobs" : "Assign Jobs"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Job Assignment Dialog */}
      <JobAssignmentDialog
        open={jobAssignmentDialogOpen}
        onClose={handleJobAssignmentDialogClose}
        job={selectedJobForAssignment}
        onSuccess={handleJobAssignmentSuccess}
        onError={(message) => setError(message)}
      />
    </Box>
  );
};
